/**
 * Bulk Edit Modal - batch update condition, finish, and language for selected cards.
 * Fields default to "No change" — only explicitly changed fields are included in the patch.
 */
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import Modal from '@/components/Modal/Modal';
import { useBatchUpdateCardsMutation } from '@/features/collection/hooks/api';
import { CONDITION_OPTIONS, FINISH_LABELS, LANGUAGE_OPTIONS } from '@/lib/cardUtils';
import type { Condition, Finish, Language } from '@/types/card';

export interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCardIds: string[];
  onSuccess: () => void;
}

const FINISH_OPTIONS: { value: Finish; label: string }[] = [
  { value: 'nonfoil', label: FINISH_LABELS.nonfoil },
  { value: 'foil', label: FINISH_LABELS.foil },
  { value: 'etched', label: FINISH_LABELS.etched },
];

const NO_CHANGE = '__no_change__' as const;

export default function BulkEditModal({ isOpen, onClose, selectedCardIds, onSuccess }: BulkEditModalProps): ReactElement {
  const [condition, setCondition] = useState<Condition | typeof NO_CHANGE>(NO_CHANGE);
  const [finish, setFinish] = useState<Finish | typeof NO_CHANGE>(NO_CHANGE);
  const [language, setLanguage] = useState<Language | typeof NO_CHANGE>(NO_CHANGE);

  const batchUpdate = useBatchUpdateCardsMutation();

  const hasChanges = condition !== NO_CHANGE || finish !== NO_CHANGE || language !== NO_CHANGE;
  const count = selectedCardIds.length;

  const handleApply = useCallback(() => {
    const patch: Record<string, unknown> = {};
    if (condition !== NO_CHANGE) patch.condition = condition;
    if (finish !== NO_CHANGE) patch.finish = finish;
    if (language !== NO_CHANGE) patch.language = language;

    batchUpdate.mutate(
      { cardIds: selectedCardIds, patch },
      {
        onSuccess: () => {
          toast.success(`Updated ${count.toLocaleString()} cards`);
          handleClose();
          onSuccess();
        },
        onError: (err) => {
          toast.error(`Failed to update cards: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  }, [condition, finish, language, selectedCardIds, batchUpdate, count, onSuccess]);

  const handleClose = useCallback(() => {
    setCondition(NO_CHANGE);
    setFinish(NO_CHANGE);
    setLanguage(NO_CHANGE);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit ${count.toLocaleString()} cards`} size="sm">
      <div className="space-y-5">
        {/* Condition */}
        <div>
          <label htmlFor="bulk-condition" className="text-silver mb-1.5 block text-sm font-medium">
            Condition
          </label>
          <select
            id="bulk-condition"
            value={condition}
            onChange={(e) => setCondition(e.target.value as Condition | typeof NO_CHANGE)}
            className="border-storm bg-slate text-pearl focus:border-aether w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none"
          >
            <option value={NO_CHANGE}>No change</option>
            {CONDITION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Finish */}
        <div>
          <label className="text-silver mb-1.5 block text-sm font-medium">Finish</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFinish(NO_CHANGE)}
              className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                finish === NO_CHANGE ? 'bg-aether/20 text-pearl border-aether/40 border' : 'bg-slate text-ash border-storm border'
              }`}
            >
              No change
            </button>
            {FINISH_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setFinish(o.value)}
                className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                  finish === o.value ? 'bg-aether/20 text-pearl border-aether/40 border' : 'bg-slate text-ash border-storm border'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Language */}
        <div>
          <label htmlFor="bulk-language" className="text-silver mb-1.5 block text-sm font-medium">
            Language
          </label>
          <select
            id="bulk-language"
            value={language}
            onChange={(e) => setLanguage(e.target.value as Language | typeof NO_CHANGE)}
            className="border-storm bg-slate text-pearl focus:border-aether w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none"
          >
            <option value={NO_CHANGE}>No change</option>
            {LANGUAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="text-silver hover:text-pearl rounded-lg px-4 py-2 text-sm transition-colors"
            disabled={batchUpdate.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!hasChanges || batchUpdate.isPending}
            className="bg-aether hover:bg-aether/80 text-pearl rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {batchUpdate.isPending ? `Updating ${count.toLocaleString()} cards...` : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
